"use client";

import type {
    LeaderboardResponse,
    PuzzleTypeFilter,
} from "@/app/oyun/_types/leaderboard";
import Pagination from "@/components/pagination";
import { Badge } from "@/components/shadcn/badge";
import { Card, CardContent } from "@/components/shadcn/card";
import { Separator } from "@/components/shadcn/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/shadcn/tabs";
import { Trophy } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

const ITEMS_PER_PAGE = 10;
// The widget sits in a ~300px column: five page slots and no first/last
// chevrons is what fits on one row.
const PAGE_LINK_SIBLING_COUNT = 0;

export default function PuzzleLeaderboard() {
    const t = useTranslations("home");
    const [filter, setFilter] = useState<PuzzleTypeFilter>("all");
    const [leaderboard, setLeaderboard] = useState<LeaderboardResponse>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        let cancelled = false;

        fetch(`/api/puzzle/get-leaderboard/${filter}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to fetch leaderboard");
                }
                return response.json();
            })
            .then((data: LeaderboardResponse) => {
                if (cancelled) return;
                setLeaderboard(data);
                setIsLoading(false);
            })
            .catch((fetchError) => {
                if (cancelled) return;
                console.error("Error fetching leaderboard:", fetchError);
                setHasError(true);
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [filter]);

    const handleFilterChange = (value: string) => {
        setFilter(value as PuzzleTypeFilter);
        setIsLoading(true);
        setHasError(false);
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(leaderboard.length / ITEMS_PER_PAGE);
    const paginated = leaderboard.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
    );

    return (
        <Card className="h-full" data-testid="homepage-puzzle-leaderboard">
            <CardContent className="flex h-full flex-col gap-4">
                <div className="flex items-center gap-2">
                    <Trophy
                        className="text-primary size-5"
                        aria-hidden="true"
                    />
                    <h3 className="font-medium">{t("puzzleLeaderboard")}</h3>
                </div>
                <Separator />
                <Tabs value={filter} onValueChange={handleFilterChange}>
                    <TabsList className="w-full">
                        <TabsTrigger value="all">{t("all")}</TabsTrigger>
                        <TabsTrigger value="positional">
                            {t("filterPositional")}
                        </TabsTrigger>
                        <TabsTrigger value="tactic">
                            {t("filterTactic")}
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
                {isLoading ? (
                    <ul className="flex flex-col gap-3" aria-hidden="true">
                        {Array.from({ length: ITEMS_PER_PAGE }, (_, index) => (
                            <li
                                key={index}
                                className="bg-muted h-5 w-full animate-pulse rounded-md"
                            />
                        ))}
                    </ul>
                ) : hasError ? (
                    <p className="text-destructive py-4 text-center text-sm">
                        {t("leaderboardError")}
                    </p>
                ) : paginated.length === 0 ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                        {t("leaderboardEmpty")}
                    </p>
                ) : (
                    <ol className="flex flex-1 flex-col gap-1.5">
                        {paginated.map((rank, index) => (
                            <li
                                key={rank.userId}
                                className="flex items-center justify-between gap-4 py-0.5"
                            >
                                <span className="truncate text-sm font-medium">
                                    {(currentPage - 1) * ITEMS_PER_PAGE +
                                        index +
                                        1}
                                    . {rank.nickName}
                                </span>
                                <Badge
                                    variant="secondary"
                                    className="bg-primary/10 text-primary"
                                >
                                    {rank.rating}
                                </Badge>
                            </li>
                        ))}
                    </ol>
                )}
                {totalPages > 1 && !isLoading && !hasError && (
                    <Pagination
                        className="mt-auto"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        siblingCount={PAGE_LINK_SIBLING_COUNT}
                        showFirstLast={false}
                    />
                )}
            </CardContent>
        </Card>
    );
}
